import { Badge } from "@/components/ui/badge";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";
import { usePlanAndCredits } from "@/hooks/use-plan-and-credits";
import { formatCurrency, formatInterval, formatNumber } from "@/lib/utils";
import { Zap, CreditCard, Calendar, CheckCircle } from "lucide-react";

export default function BillingOverview({
  state,
  subscriptions,
}: {
  state: ReturnType<typeof usePlanAndCredits>;
  subscriptions: any[];
}) {
  const { currentPlan, creditsData, usageData, loading } = state;
  const currentUsage = usageData?.currentUsage;
  const subscription = subscriptions?.[0];

  return currentPlan || creditsData.remaining > 0 ? (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Current Plan Card */}
      {currentPlan && (
        <Card className=" bg-gradient-to-br bg-purple-500 shadow-lg">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                  <CreditCard className="w-5 h-5 text-white" />
                </div>
                <div>
                  <CardTitle className="text-white">Current Plan</CardTitle>
                  <CardDescription className="text-purple-100">
                    {currentPlan.name}
                  </CardDescription>
                </div>
              </div>
              <Badge
                variant="secondary"
                className="bg-white/20 text-white border-white/30 backdrop-blur-sm"
              >
                {subscription?.status || "Active"}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {currentPlan?.price > 0 && (
                <div className="flex items-center justify-between">
                  <span className="text-purple-100 font-medium">Plan Price</span>
                  <span className="text-white font-bold text-lg">
                    {formatCurrency(currentPlan.price, currentPlan.currency)} /{" "}
                    {formatInterval(currentPlan.interval || "month")}
                  </span>
                </div>
              )}
              {subscription?.current_period_end && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Calendar className="w-4 h-4 text-white" />
                    <span className="text-purple-100 font-medium">Next Billing</span>
                  </div>
                  <span className="text-white font-semibold">
                    {new Date(
                      subscription.current_period_end * 1000
                    ).toLocaleDateString()}
                  </span>
                </div>
              )}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-300" />
                  <span className="text-purple-100 font-medium">Status</span>
                </div>
                <span className="text-green-300 font-semibold">
                  {currentPlan.active ? "Active" : "Inactive"}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Credits Card - Redesigned */}
      <Card className=" bg-gradient-to-br bg-purple-500 shadow-lg">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                <Zap className="w-5 h-5 text-white" />
              </div>
              <div>
                <CardTitle className="text-white">API Credits</CardTitle>
                <CardDescription className="text-purple-100">
                  {creditsData.unlimited
                    ? "Unlimited usage available"
                    : "Your available credits"}
                </CardDescription>
              </div>
            </div>
            <Badge
              variant="secondary"
              className="bg-white/20 text-white border-white/30 backdrop-blur-sm"
            >
              {creditsData.unlimited ? "Unlimited" : "Active"}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {/* Main Credits Display */}
            <div className="text-center">
              <div className="text-4xl font-bold text-white mb-2">
                {creditsData.unlimited ? "∞" : formatNumber(creditsData.remaining)}
              </div>
              <div className="text-purple-100 font-medium">
                {creditsData.unlimited ? "Unlimited Credits" : "Credits Remaining"}
              </div>
            </div>
            
            {/* Status Indicator */}
            <div className="flex items-center justify-center space-x-2">
              <CheckCircle className="w-4 h-4 text-green-300" />
              <span className="text-purple-100 text-sm">
                {creditsData.unlimited
                  ? "No usage limits"
                  : creditsData.remaining > 5000
                    ? "Plenty of credits available"
                    : creditsData.remaining > 500
                      ? "Good amount remaining"
                      : "Consider topping up soon"}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  ) : (
    !loading && (
      <Card className="bg-white/10 backdrop-blur-sm border-white/20 shadow-lg">
        <CardContent className="flex items-center justify-center py-12">
          <div className="text-center space-y-4">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto backdrop-blur-sm">
              <Zap className="w-8 h-8 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white mb-2">
                Ready to Get Started?
              </h3>
              <p className="text-purple-100 mb-4">
                Choose a plan below to start using our API services
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    )
  );
}
